import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, Coins, Bitcoin, LineChart, Landmark } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtPct, fmtUSD, fmtUSDExact } from "@/lib/format";

export default function Dashboard() {
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState({ cash: 0, holdings: [], total_value: 0, total_market_value: 0 });
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
    api.get("/markets/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/transactions", { params: { limit: 8 } }).then(({ data }) => setTxs(data)).catch(() => {});
  }, []);

  // Simple perf series (synthetic from holdings)
  const chartData = React.useMemo(() => {
    const total = portfolio.total_value || 0;
    const arr = [];
    for (let i = 0; i < 24; i++) {
      const factor = 1 + Math.sin(i / 3 + total) * 0.02 + i * 0.001;
      arr.push({ t: `${i}:00`, v: +(total * factor).toFixed(2) });
    }
    return arr;
  }, [portfolio.total_value]);

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="dashboard-page">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            // overview
          </div>
          <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase">
            {greet(user?.name)}
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="dashboard-go-markets"
            onClick={() => nav("/app/markets")}
            className="border border-neo-border px-5 py-3 font-mono uppercase tracking-widest text-xs hover:bg-white/5 transition-colors"
          >
            Markets →
          </button>
          <button
            data-testid="dashboard-go-billing"
            onClick={() => nav("/app/billing")}
            className="bg-white text-black px-5 py-3 font-mono uppercase tracking-widest text-xs border border-white hover-invert"
          >
            + Top up
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <BigStat
          label="Total value"
          value={fmtUSDExact(portfolio.total_value)}
          note={`cash ${fmtUSDExact(portfolio.cash)}`}
          testid="stat-total-value"
        />
        <BigStat
          label="Holdings"
          value={fmtUSDExact(portfolio.total_market_value)}
          note={`${portfolio.holdings.length} positions`}
          testid="stat-holdings-value"
        />
        <BigStat
          label="Available cash"
          value={fmtUSDExact(portfolio.cash)}
          note="ready to trade"
          testid="stat-cash"
        />
        <BigStat
          label="Trades executed"
          value={String(txs.length >= 8 ? "8+" : txs.length)}
          note="last 8 shown below"
          testid="stat-trades"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 border border-neo-border bg-neo-surface p-6" data-testid="dashboard-perf-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              // portfolio_perf · synthetic
            </div>
            <div className="font-mono text-xs text-neutral-500">24h view</div>
          </div>
          <div className="h-64 min-h-[16rem] w-full">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <ReLineChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                <CartesianGrid stroke="#262626" strokeDasharray="0" />
                <XAxis dataKey="t" stroke="#8F8F9D" fontSize={10} tickLine={false} axisLine={{ stroke: "#262626" }} />
                <YAxis stroke="#8F8F9D" fontSize={10} tickLine={false} axisLine={{ stroke: "#262626" }} />
                <Tooltip
                  contentStyle={{
                    background: "#141414",
                    border: "1px solid #262626",
                    borderRadius: 0,
                    fontFamily: "JetBrains Mono",
                    fontSize: 12,
                  }}
                />
                <Line type="monotone" dataKey="v" stroke="#00FF66" strokeWidth={2} dot={false} isAnimationActive />
              </ReLineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-neo-border bg-neo-surface p-6" data-testid="dashboard-market-summary">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
            // universe
          </div>
          <UniverseRow icon={<Bitcoin className="w-4 h-4" />} label="Crypto" count={summary?.crypto_count} />
          <UniverseRow icon={<LineChart className="w-4 h-4" />} label="Stocks" count={summary?.stock_count} />
          <UniverseRow icon={<Landmark className="w-4 h-4" />} label="Bonds" count={summary?.bond_count} />
          <UniverseRow icon={<Coins className="w-4 h-4" />} label="Fee rate" count="1.00%" />
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-neo-border bg-neo-surface" data-testid="dashboard-top-crypto">
          <div className="p-4 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            // top_crypto
          </div>
          <MiniList rows={summary?.top_crypto || []} />
        </div>
        <div className="border border-neo-border bg-neo-surface" data-testid="dashboard-recent-tx">
          <div className="p-4 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex justify-between">
            <span>// recent_trades</span>
            <span className="text-neutral-600">{txs.length}</span>
          </div>
          <div className="divide-y divide-neo-border">
            {txs.length === 0 && (
              <div className="p-6 text-neutral-500 text-sm font-mono">
                No trades yet. Head to markets →
              </div>
            )}
            {txs.map((t) => (
              <div key={t.id} className="p-4 flex items-center justify-between font-mono text-xs">
                <div>
                  <div className="text-white">
                    {t.side.toUpperCase()} {t.symbol}{" "}
                    <span className="text-neutral-500">· {t.asset_type}</span>
                  </div>
                  <div className="text-neutral-500 text-[10px] mt-1">
                    {new Date(t.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-right">
                  <div className={t.side === "buy" ? "neg" : "pos"}>
                    {t.side === "buy" ? "-" : "+"}
                    {fmtUSDExact(t.total)}
                  </div>
                  <div className="text-neutral-500 text-[10px] mt-1">
                    qty {t.quantity} · fee {fmtUSDExact(t.fee)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function greet(name) {
  const h = new Date().getHours();
  const salutation = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  return `${salutation}, ${name || "trader"}.`;
}

function BigStat({ label, value, note, testid }) {
  return (
    <div className="border border-neo-border bg-neo-surface p-6" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</div>
      <div className="mt-3 font-mono text-3xl font-bold tracking-tighter">{value}</div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        {note}
      </div>
    </div>
  );
}

function UniverseRow({ icon, label, count }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-neo-border last:border-b-0">
      <div className="flex items-center gap-3 text-neutral-300">
        {icon}
        <span className="font-mono text-xs uppercase tracking-widest">{label}</span>
      </div>
      <span className="font-mono text-lg font-bold">{count ?? "—"}</span>
    </div>
  );
}

function MiniList({ rows }) {
  return (
    <div className="divide-y divide-neo-border">
      {rows.slice(0, 6).map((r) => (
        <div key={r.symbol} className="p-4 flex items-center justify-between font-mono text-xs">
          <div className="flex items-center gap-3">
            <span className="text-neutral-500 uppercase w-6">{r.symbol}</span>
            <span className="text-white">{r.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-neutral-200">{fmtUSD(r.price)}</span>
            <span className={r.change_24h >= 0 ? "pos flex items-center gap-1" : "neg flex items-center gap-1"}>
              {r.change_24h >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {fmtPct(r.change_24h)}
            </span>
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="p-6 text-neutral-500 text-sm font-mono">loading…</div>}
    </div>
  );
}
